package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** One stop in a flow that has a fixed shape: import, connect a repository, confirm. */
class LStep(val label: String, val description: String? = null)

/** What a step is, which decides its mark, its colour and what a reader is told about it. */
enum class LStepState { DONE, CURRENT, UPCOMING }

/**
 * Horizontal where there is room, vertical where there is not.
 *
 * Not a scroll: three or four steps laid out in a row on a 390px screen shrink each label to two
 * words and a hyphen, and the point of the component is that a reader can see the whole shape of
 * the flow at once. Below MD the same list stacks, which costs vertical space and keeps the labels.
 */
val LStepsStyle = CssStyle {
    base {
        Modifier
            .width(100.percent)
            .gap(Space.MD)
            .margin(0.px)
            .padding(0.px)
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
                property("list-style", "none")
            }
    }
    Breakpoint.MD {
        Modifier.gap(Space.XL).styleModifier { property("flex-direction", "row") }
    }
}

val LStepStyle = CssStyle.base {
    Modifier
        .gap(Space.SM)
        .alignItems(AlignItems.FlexStart)
        .styleModifier {
            property("display", "flex")
            property("flex", "1 1 0")
            property("min-width", "0")
        }
}

/**
 * The mark: a check when the step is behind you, its own number when it is not.
 *
 * Current is an accent **ring** rather than an accent fill. A filled disc is what done looks like
 * everywhere else in this product, and two filled discs of different colours in one row is a
 * palette puzzle rather than a progress indicator. The ring says "you are here" with the one
 * accent, and the number inside it stays legible on the page's own ground.
 */
val LStepMarkerStyle = CssStyle {
    base {
        Modifier
            .width(24.px)
            .height(24.px)
            .borderRadius(Radius.Pill)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
            .color(colorMode.toSitePalette().textMuted)
            .fontSize(TypeScale.Small)
            .fontWeight(600)
            .lineHeight(1)
            .tabularNumbers()
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("flex", "0 0 auto")
            }
    }
    cssRule("[data-step-state=\"done\"]") {
        Modifier
            .color(colorMode.toSitePalette().accent)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().accent)
    }
    cssRule("[data-step-state=\"current\"]") {
        Modifier
            .color(colorMode.toSitePalette().accent)
            .border(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
    }
}

val LStepLabelStyle = CssStyle {
    base {
        Modifier
            .color(colorMode.toSitePalette().textMuted)
            .fontSize(TypeScale.BodySmall)
            .fontWeight(600)
            .lineHeight(TypeScale.BodyLineHeight)
    }
    cssRule("[data-step-state=\"done\"]") { Modifier.color(colorMode.toSitePalette().textSecondary) }
    cssRule("[data-step-state=\"current\"]") { Modifier.color(colorMode.toSitePalette().textPrimary) }
}

val LStepDescriptionStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textSecondary)
        .fontSize(TypeScale.Small)
        .lineHeight(TypeScale.BodyLineHeight)
}

/**
 * Where the reader is in a flow with a known shape.
 *
 * An `<ol>`, because these are ordered and a reader who cannot see the row still needs the order.
 * `aria-current="step"` marks exactly one item — the one thing the ARIA value exists to say — and
 * every item also carries a visually-hidden "Step 2 of 3, current step", because the accent ring
 * and the check glyph are colour and shape, and neither is announced.
 *
 * [completed] rather than "everything before `current`": a flow can be re-entered at step one with
 * step three already done, and a component that infers completion from position renders that as
 * three steps of undone work.
 *
 * @param current the index that is being worked on now. Out of range means no step is current,
 *   which is the honest state for a flow that has finished.
 */
@Composable
fun LSteps(
    steps: List<LStep>,
    current: Int,
    modifier: Modifier = Modifier,
    completed: Set<Int> = emptySet(),
    label: String = t("ds.ds.lsteps.steps", "Steps"),
) {
    val doneWord = t("ds.ds.lsteps.completed", "Completed")
    val currentWord = t("ds.ds.lsteps.current_step", "Current step")
    val upcomingWord = t("ds.ds.lsteps.not_started", "Not started")

    Ol(
        LStepsStyle.toModifier().then(modifier).toAttrs {
            attr("aria-label", label)
            attr("data-steps", "")
        },
    ) {
        steps.forEachIndexed { index, step ->
            // [completed], not "everything before [current]". A flow re-entered at step one with
            // step three already finished is a real shape — the import wizard has it — and a
            // component that infers completion from position redraws that work as undone.
            val state = when {
                index == current -> LStepState.CURRENT
                index in completed -> LStepState.DONE
                else -> LStepState.UPCOMING
            }
            val stateAttribute = state.name.lowercase()
            Li(
                LStepStyle.toModifier().toAttrs {
                    attr("data-step-state", stateAttribute)
                    // Exactly one item. `aria-current="step"` on every finished step as well
                    // announces the reader as being in three places at once.
                    if (state == LStepState.CURRENT) attr("aria-current", "step")
                },
            ) {
                Span(
                    LStepMarkerStyle.toModifier().toAttrs {
                        attr("data-step-state", stateAttribute)
                        attr("aria-hidden", "true")
                    },
                ) {
                    if (state == LStepState.DONE) {
                        LCircleCheckIcon(Modifier.iconSize(IconSize.MD))
                    } else {
                        Text((index + 1).toString())
                    }
                }
                Span(
                    Modifier
                        .gap(Space.XXS)
                        .styleModifier {
                            property("display", "flex")
                            property("flex-direction", "column")
                            property("min-width", "0")
                        }
                        .toAttrs(),
                ) {
                    Span(
                        LStepLabelStyle.toModifier().toAttrs {
                            attr("data-step-state", stateAttribute)
                        },
                    ) {
                        Text(step.label)
                    }
                    if (step.description != null) {
                        Span(LStepDescriptionStyle.toModifier().toAttrs()) { Text(step.description) }
                    }
                    Span(LVisuallyHiddenStyle.toModifier().toAttrs()) {
                        Text(
                            tf(
                                "ds.ds.lsteps.step_number_of_total_status",
                                "Step {number} of {total}, {status}",
                                "number" to (index + 1),
                                "total" to steps.size,
                                "status" to when (state) {
                                    LStepState.DONE -> doneWord
                                    LStepState.CURRENT -> currentWord
                                    LStepState.UPCOMING -> upcomingWord
                                },
                            ),
                        )
                    }
                }
            }
        }
    }
}
